// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// The agent session -- the pure orchestration "brain" the Editor drives (WS4
// Phase 1c-iii). It wraps the conversation with the transient session state the
// UI needs (active persona, busy, and the proposed models awaiting a decision)
// and exposes reducer-style transitions. The Editor stays a thin imperative
// shell: it generates ids, calls the injected ProposeFn, and applies the model
// `decide` hands back on accept.
//
// The H2 cancel-wins race lives here: applyResult only tracks a pending model
// when resolveTurn actually advanced the turn (it is a no-op if the user
// cancelled first), so a late propose result can neither overwrite a cancel nor
// leave an un-cancellable pending proposal.

import type { JsonModel } from '@simlin/engine';

import type { AgentPersona } from './agent-personas';
import {
  type Conversation,
  type ProposeResult,
  type ProposalSummary,
  EMPTY_CONVERSATION,
  appendUser,
  appendWorking,
  resolveTurn,
  cancelTurn,
  decideProposal,
} from './agent-conversation';

/**
 * A proposal accepted and awaiting application: the proposed whole-model snapshot
 * plus its diff. The Editor applies ONLY the diff (upsert added/changed, delete
 * removed) rather than the whole snapshot, so a concurrent manual edit made while
 * the agent was generating is not clobbered (H2), and Merlin's removals are exact.
 */
export interface PendingApply {
  readonly model: JsonModel;
  readonly proposal: ProposalSummary;
}

export interface AgentSession {
  readonly conversation: Conversation;
  readonly activePersona: AgentPersona;
  readonly busy: boolean;
  /** Proposals awaiting a decision, keyed by proposalId (applied on accept). */
  readonly pending: ReadonlyMap<string, PendingApply>;
}

export function initialSession(
  activePersona: AgentPersona = 'merlin',
  conversation: Conversation = EMPTY_CONVERSATION,
): AgentSession {
  return { conversation, activePersona, busy: false, pending: new Map() };
}

export function selectPersona(session: AgentSession, persona: AgentPersona): AgentSession {
  return { ...session, activePersona: persona };
}

/** Begin a send: append the user message + a working agent turn; mark busy. */
export function startSend(
  session: AgentSession,
  ids: { userTurnId: string; agentTurnId: string },
  text: string,
): AgentSession {
  let conversation = appendUser(session.conversation, ids.userTurnId, text);
  conversation = appendWorking(conversation, ids.agentTurnId, session.activePersona);
  return { ...session, conversation, busy: true };
}

/**
 * Fold a propose result into the working turn. If the turn was cancelled first,
 * resolveTurn is a no-op and no pending model is tracked (cancel wins, H2).
 */
export function applyResult(session: AgentSession, agentTurnId: string, result: ProposeResult): AgentSession {
  const conversation = resolveTurn(session.conversation, agentTurnId, result.result);
  const advanced = conversation !== session.conversation;
  let pending: ReadonlyMap<string, PendingApply> = session.pending;
  if (advanced && result.result.kind === 'proposal' && result.proposedModel) {
    const next = new Map(session.pending);
    next.set(result.result.proposal.proposalId, { model: result.proposedModel, proposal: result.result.proposal });
    pending = next;
  }
  return { ...session, conversation, pending, busy: false };
}

/** Cancel the working turn (stop mid-stream). Clears busy. */
export function cancel(session: AgentSession, agentTurnId: string): AgentSession {
  return { ...session, conversation: cancelTurn(session.conversation, agentTurnId), busy: false };
}

/**
 * Record an accept/reject on a pending proposal. On accept, returns the model to
 * apply (and drops it from pending); on reject, drops it with nothing to apply. A
 * no-op decision (already decided) returns the session unchanged and no model.
 */
export function decide(
  session: AgentSession,
  agentTurnId: string,
  proposalId: string,
  decision: 'accepted' | 'rejected',
): { session: AgentSession; apply?: PendingApply } {
  const conversation = decideProposal(session.conversation, agentTurnId, decision);
  if (conversation === session.conversation) {
    return { session };
  }
  const pending = new Map(session.pending);
  const entry = pending.get(proposalId);
  pending.delete(proposalId);
  const next: AgentSession = { ...session, conversation, pending };
  return { session: next, apply: decision === 'accepted' ? entry : undefined };
}

/**
 * Fold a propose result into the working turn and, when `inPlace` is set
 * (Advanced tier), immediately auto-accept a proposal so it applies with no
 * preview step -- returning the model to apply. In the default (preview) case,
 * or for a non-proposal result, it just resolves the turn (leaving a proposal
 * pending for the user's accept/reject). The cancel-wins guard still holds: if
 * the user cancelled first, applyResult is a no-op and the auto-accept then finds
 * no pending proposal, so nothing applies (H2).
 */
export function resolveAndMaybeApply(
  session: AgentSession,
  agentTurnId: string,
  result: ProposeResult,
  inPlace: boolean,
): { session: AgentSession; apply?: PendingApply } {
  const resolved = applyResult(session, agentTurnId, result);
  if (!inPlace || result.result.kind !== 'proposal') {
    return { session: resolved };
  }
  return decide(resolved, agentTurnId, result.result.proposal.proposalId, 'accepted');
}
